import { FC, useContext } from 'react';

import { AlienIcon, BonusIcon, Label } from '@smartshell/ui-kit';
import cn from 'classnames';
import day from 'dayjs';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EPaymentItemType, EPaymentStatus, TPayment } from 'types';
import { formatSum, getPaymentMethodLabel } from 'utils';

import { RefundButton } from '../RefundButton';
import { ITableMobileProps } from './interfaces';
import s from './TableMobile.module.scss';

export const TableMobile: FC<ITableMobileProps> = ({
  data,
  setNotPermissionModalOpen,
  setClientId,
  setSelectedPaymentId,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  return (
    <div
      className={s.container}
      onClick={(event) => {
        const isClickableElement = (event.target as HTMLElement).closest(
          '[data-prevent-row-click]'
        );

        if (isClickableElement) return;

        setSelectedPaymentId(data?.id ? data.id : null);
      }}
    >
      <div className={s.top}>
        <div className={s.date}>
          <span className={cn(s.text)}>
            {day(data?.created_at).format('DD.MM.YYYY')}
          </span>
          <span className={cn(s.text, s.bold)}>
            {day(data?.created_at).format('HH:mm:ss')}
          </span>
        </div>
        <div className={s.right}>
          <span>{getPaymentMethodLabel(data?.method, t)}</span>
          {(() => {
            if (data?.is_refunded) {
              return (
                <Label
                  color="gray-primary"
                  iconName="CircleCloseIcon"
                  rounded
                />
              );
            }

            switch (data?.status) {
              case EPaymentStatus.PENDING:
                return <Label color="orange" iconName="ClockIcon" rounded />;
              case EPaymentStatus.UNPAID:
                return <Label color="red" iconName="CancelIcon" rounded />;
            }

            return permissions?.has('refund_create') &&
              !data?.not_refundable ? (
              <RefundButton
                payment={data as TPayment}
                disabled={!permissions?.has('refund_create')}
                onlyIcon
              />
            ) : null;
          })()}
        </div>
      </div>

      <div className={s.bottom}>
        <div className={s.left}>
          <AlienIcon size="s" />
          {data?.client ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (!permissions?.has('client_info')) {
                  setNotPermissionModalOpen(true);
                  return;
                }
                if (data?.client?.uuid) setClientId(data?.client?.uuid);
              }}
              className={s.client}
            >
              {data?.client?.nickname
                ? data?.client?.nickname
                : data?.client?.phone}
            </span>
          ) : (
            <span className={s.ghost}>{t('client.guest')}</span>
          )}
        </div>

        <div className={s.right}>
          {data?.items[0]?.type === EPaymentItemType.BONUS ? (
            <div className={s.bonuses}>
              <BonusIcon size="s" />
              <span className={s.sum}>{data?.sum}</span>
            </div>
          ) : (
            <div className={s.money}>
              <span className={s.sum}>
                {formatSum(data?.sum, currency.sign)}
              </span>
              {Boolean(data?.bonus) && (
                <Label
                  iconName={
                    data?.items?.some(
                      (i) => i.type === EPaymentItemType.DEPOSIT
                    )
                      ? 'BonusAddIcon'
                      : 'BonusRemoveIcon'
                  }
                  size="s"
                  color={
                    data?.items?.some(
                      (i) => i.type === EPaymentItemType.DEPOSIT
                    )
                      ? 'green'
                      : 'red'
                  }
                  text={String(data?.bonus)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
